import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import { faDollar, faEdit, faPercent, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPercent as faPercentSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignPromoCodes } from 'api/campaigns';
import { DeletePromoCodeAlert } from 'assets/js/pages/campaign-settings/campaign-event/DeletePromoCodeAlert';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { PromoCode } from 'types/domain';

const columnHelper = createColumnHelper<PromoCode>();

const EventPromoCodes = () => {
    const history = useHistory();
    const currency = useCurrencySymbol();
    const [search, setSearch] = usePersistentSearch();
    const searchQuery = useDebounce(search);
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();

    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            query: searchQuery || undefined,
            page: pagination.pageIndex + 1,
        },
        500,
    );
    const { data: promoCodes } = useSuspenseQuery({
        queryKey: campaignQueryKeys.promoCodesFiltered(campaign.id, params),
        queryFn: () => fetchCampaignPromoCodes(campaign.id, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('code', {
                header: 'Code',
                cell: info => <p className={'line-clamp-1 font-medium'}>{info.getValue()}</p>,
            }),
            columnHelper.accessor('used', {
                header: 'Qty',
                cell: ({ row, getValue }) => (
                    <p>
                        {getValue()}
                        {' / '}
                        {row.original.uses === null ? 'UNL' : row.original.uses}
                    </p>
                ),
            }),
            columnHelper.accessor('total_discount', {
                header: 'Discounts Applied',
                cell: ({ getValue }) => (
                    <p>
                        {currency}
                        {maskDecimal(getValue())}
                    </p>
                ),
            }),
            columnHelper.accessor('type', {
                header: 'Type',
                cell: ({ getValue }) => (
                    <p>
                        <FontAwesomeIcon
                            className={'mr-2'}
                            icon={getValue() === 'percentage' ? faPercent : faDollar}
                        />
                        {getValue() === 'percentage' ? 'Percentage' : 'Dollar'}
                    </p>
                ),
            }),
            columnHelper.accessor('active', {
                header: 'Status',
                cell: ({ getValue }) => (
                    <>
                        {getValue() ? (
                            <p
                                className={
                                    'rounded-full bg-green-50 px-2 py-1 font-medium text-green-500'
                                }
                            >
                                Active
                            </p>
                        ) : (
                            <p className={'rounded-full bg-gray-50 px-2 py-1 font-medium'}>
                                Inactive
                            </p>
                        )}
                    </>
                ),
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: ({ row }) => (
                    <DropdownMenu.Root>
                        <Table.TableRowDropdownTrigger />
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.Item
                                    onClick={() => {
                                        history.push(
                                            `/accounts/${accountId}/campaigns/${campaign.id}/event/promo-codes/${row.original.id}/edit`,
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEdit} className={'mr-2'} />
                                    Edit
                                </DropdownMenu.Item>

                                <DropdownMenu.Item
                                    className={'text-red-600 hover:!text-red-600'}
                                    onSelect={e => e.preventDefault()}
                                >
                                    <DeletePromoCodeAlert promoCode={row.original}>
                                        <p>
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className={'mr-2 text-red-700'}
                                            />
                                            Delete
                                        </p>
                                    </DeletePromoCodeAlert>
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                ),
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId, campaign.id, history, currency],
    );

    const table = useReactTable({
        data: promoCodes?.data ?? [],
        columns,
        pageCount: promoCodes?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell =>
                `/accounts/${accountId}/campaigns/${campaign.id}/event/promo-codes/${cell.row.original.id}/edit`,
        },
    });

    useSyncPaginatedData(table, promoCodes);

    return (
        <div>
            <div className={'mb-2'}>
                <h1 className={'text-2xl font-medium leading-snug'}>Promo codes</h1>
            </div>

            <div className={'mb-4 flex items-center justify-between'}>
                <Input.Root size={'sm'}>
                    <Input.SearchIcon />
                    <Input.Form
                        placeholder={'Search'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Input.Root>
                <div>
                    <Button
                        variant="solid"
                        type={'button'}
                        onClick={() =>
                            history.push(
                                `/accounts/${accountId}/campaigns/${campaign.id}/event/promo-codes/new`,
                            )
                        }
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New promo code
                    </Button>
                </div>
            </div>

            <div>
                <div className={'flex flex-col gap-y-8 overflow-x-auto'}>
                    <Table.Root
                        isLoading={isPending}
                        table={table}
                        searchTerm={searchQuery}
                        variant="bordered"
                    >
                        <Box className="overflow-x-auto">
                            <Table.Content className={'w-full'}>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'secondary'}>
                                <Table.EmptyIcon>
                                    <FontAwesomeIcon icon={faPercentSolid} />
                                </Table.EmptyIcon>
                                <Table.EmptyTitle>
                                    You haven't added any promo codes
                                </Table.EmptyTitle>
                                <Table.EmptyDescription>
                                    Create promo codes that provide your supporters with exclusive
                                    discounts when purchasing tickets to your event.
                                </Table.EmptyDescription>
                            </Table.Empty>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </Table.Root>
                </div>
            </div>
        </div>
    );
};

export { EventPromoCodes };
